import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import CognitoAuth from "../types/cognitoAuth";
import httpCognito from "../api/httpCognito";
import User from "../types/user";
import http, { authHttp } from "../api/http";
import { AwsConfigAuth } from "../helpers/authHelper";
import Company from "../types/company";
import { signOut } from "aws-amplify/auth";

interface AuthenticationState {
  status: "idle" | "loading" | "failed";
  auth?: CognitoAuth;
  loggedInUser?: User;
  profileUser?: User;
  userCompany?: Company;
}

const initialState: AuthenticationState = {
  status: "idle",
};

export const fetchDbUser = createAsyncThunk<Partial<User>>(
  "auth/fetch-dbUser",
  async () => {
    const response = await http.get<Partial<User>>(`/auth`);
    return response.data;
  }
);

export const fetchProfile = createAsyncThunk<{
  user: User;
  company: Company;
}>("auth/fetch-profile", async () => {
  const response = await authHttp.get<{
    user: User;
    company: Company;
  }>(`/user/profile`, {
    params: {
      refreshToken: true,
    },
  });
  return response.data;
});

export const logout = createAsyncThunk("auth/logout", async () => {
  await authHttp.get(`/auth/logout`).then(async () => {
    const response = await signOut();
    return response;
  });
});

export const postToken = createAsyncThunk<CognitoAuth, string>(
  "token/post",
  async (code: string) => {
    const tokenEndpoint = "/oauth2/token";
    const response = await httpCognito.post<CognitoAuth>(
      tokenEndpoint,
      {
        client_id: AwsConfigAuth.userPoolClientId,
        grant_type: "authorization_code",
        code: code,
        redirect_uri: AwsConfigAuth.oauth.redirectSignIn,
      },
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );
    return response.data;
  }
);

export const postRefreshToken = createAsyncThunk<CognitoAuth, string>(
  "refreshToken/post",
  async (refreshToken: string) => {
    const tokenEndpoint = "/oauth2/token";
    const response = await httpCognito.post<CognitoAuth>(
      tokenEndpoint,
      {
        client_id: AwsConfigAuth.userPoolClientId,
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        redirect_uri: AwsConfigAuth.oauth.redirectSignIn,
      },
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );
    return response.data;
  }
);

export const getLoggedInUser = createAsyncThunk<User, string>(
  "loggedInUser/get",
  async (email: string) => {
    const response = await http.get<User>(`/user?email=${email}`);
    return response.data;
  }
);

const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    resetAuth: (state) => {
      state.auth = initialState.auth;
    },
    clearAuth: (state) => {
      state.auth = undefined;
    },
    clearLoggedInUser: (state) => {
      state.loggedInUser = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postToken.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postToken.fulfilled, (state, action) => {
        state.status = "idle";
        state.auth = action.payload;
      })
      .addCase(postToken.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postRefreshToken.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postRefreshToken.fulfilled, (state, action) => {
        state.status = "idle";
        state.auth = {
          ...state.auth!,
          access_token: action.payload.access_token,
          id_token: action.payload.id_token,
          expires_in: action.payload.expires_in,
        };
      })
      .addCase(postRefreshToken.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getLoggedInUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getLoggedInUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.loggedInUser = action.payload;
      })
      .addCase(getLoggedInUser.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.status = "idle";
        state.profileUser = action.payload.user;
        state.userCompany = action.payload.company;
      })
      .addCase(fetchProfile.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchDbUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDbUser.fulfilled, (state, action) => {
        state.status = "idle";
        const profile = Array.isArray(state.profileUser)
          ? state.profileUser[0]
          : state.profileUser;
        const payload = Array.isArray(action.payload)
          ? action.payload[0]
          : action.payload;

        let newUser: User = { ...profile, ...payload };
        state.loggedInUser = newUser;
      })
      .addCase(fetchDbUser.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(logout.pending, (state) => {
        state.status = "loading";
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.status = "idle";
        state.loggedInUser = undefined;
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export default authenticationSlice.reducer;
export const { resetAuth, clearAuth, clearLoggedInUser } =
  authenticationSlice.actions;
